// DeviceType is used in the validation logic
import {
  type CreateWallpaperPackData,
  type DeviceValidationError
} from '../../types/device';
import { hasCriticalFormatValidationErrors } from '@/utils/validation';

/**
 * Service for validating wallpaper data and packs
 */
export class WallpaperValidationService {
  /**
   * Validates wallpaper pack data before storage
   */
  validateWallpaperPack(packData: CreateWallpaperPackData): DeviceValidationError[] {
    const errors: DeviceValidationError[] = [];

    // Basic validation
    if (!packData.title?.trim()) {
      errors.push({
        device: packData.primaryDevice,
        field: 'title',
        type: 'format',
        message: 'Title is required',
        canProceed: false
      });
    }

    if (!packData.category?.trim()) {
      errors.push({
        device: packData.primaryDevice,
        field: 'category',
        type: 'format',
        message: 'Category is required',
        canProceed: false
      });
    }

    // Device variations validation
    if (!packData.deviceVariations || packData.deviceVariations.length === 0) {
      errors.push({
        device: packData.primaryDevice,
        field: 'deviceVariations',
        type: 'format',
        message: 'At least one device variation is required',
        canProceed: false
      });
    } else {
      // Validate each device variation
      for (const variation of packData.deviceVariations) {
        // Only reject if there are critical format errors, allow quality/dimension warnings
        const hasCriticalErrors = hasCriticalFormatValidationErrors(variation.validationWarnings);

        if (!variation.imageUrl?.trim()) {
          errors.push({
            device: variation.device,
            field: 'imageUrl',
            type: 'format',
            message: `Image URL is required for ${variation.device}`,
            canProceed: false
          });
        } else if (hasCriticalErrors) {
          errors.push({
            device: variation.device,
            field: 'imageUrl',
            type: 'quality',
            message: `Critical format error for ${variation.device}`,
            suggestion: 'Please upload a valid image format',
            canProceed: false
          });
        }

        if (!variation.imageUrl?.trim()) {
          errors.push({
            device: variation.device,
            field: 'imageUrl',
            type: 'format',
            message: `Image URL is required for ${variation.device}`,
            canProceed: false
          });
        }
      }

      // Note: Multiple variations per device type are now allowed
      // No need to check for duplicate device types

      // Validate primary device exists in variations
      if (!packData.deviceVariations.some(v => v.device === packData.primaryDevice)) {
        errors.push({
          device: packData.primaryDevice,
          field: 'primaryDevice',
          type: 'format',
          message: 'Primary device must have at least one variation',
          canProceed: false
        });
      }
    }

    // Validate supported devices
    if (!packData.supportedDevices || packData.supportedDevices.length === 0) {
      errors.push({
        device: packData.primaryDevice,
        field: 'supportedDevices',
        type: 'format',
        message: 'At least one supported device is required',
        canProceed: false
      });
    }

    // Validate primary device is in supported devices
    if (packData.supportedDevices && !packData.supportedDevices.includes(packData.primaryDevice)) {
      errors.push({
        device: packData.primaryDevice,
        field: 'primaryDevice',
        type: 'format',
        message: 'Primary device must be in supported devices list',
        canProceed: false
      });
    }

    return errors;
  }

  /**
   * Validates basic wallpaper data
   */
  validateWallpaperData(data: {
    title?: string;
    category?: string;
    imageUrl?: string;
    tags?: string[];
  }): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.title?.trim()) {
      errors.push('Title is required');
    }

    if (!data.category?.trim()) {
      errors.push('Category is required');
    }

    if (!data.imageUrl?.trim()) {
      errors.push('Image URL is required');
    }

    if (data.tags && data.tags.some(tag => !tag.trim())) {
      errors.push('All tags must be non-empty');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export const wallpaperValidationService = new WallpaperValidationService();